
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Code, RefreshCw } from "lucide-react";

import { ConversationFilters } from "./ConversationFilters";
import { ConversationsTable } from "./ConversationsTable";
import { ConversationDetailSidebar } from "./ConversationDetailSidebar";
import { ConversationRawData } from "./ConversationRawData";
import { 
  truncateId, 
  getAudioUrlFromData 
} from "./conversationUtils";
import { useConversationsData } from "./hooks/useConversationsData";
import { useConversationDetails } from "./hooks/useConversationDetails";

const ConversationsList = () => {
  const [showRawData, setShowRawData] = useState(false);
  
  // Use custom hooks
  const { 
    filteredConversations, 
    rawResponse, 
    isLoading, 
    isLinking, 
    statusFilter, 
    setStatusFilter, 
    searchTerm, 
    setSearchTerm, 
    fetchConversations, 
    handleLinkToLead 
  } = useConversationsData();
  
  const { 
    selectedConversation, 
    conversationDetails, 
    isLoadingDetails, 
    currentTab, 
    setCurrentTab, 
    audioData, 
    isLoadingAudio, 
    sidebarOpen, 
    setSidebarOpen, 
    handleViewDetails, 
    handleFetchAudio 
  } = useConversationDetails();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent Conversations</CardTitle>
          <CardDescription>List of recent ElevenLabs conversations and calls</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => setShowRawData(!showRawData)} 
            title="Toggle raw response data"
          >
            <Code className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={fetchConversations} 
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ConversationFilters 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        <ConversationsTable 
          conversations={filteredConversations}
          isLoading={isLoading}
          selectedConversation={selectedConversation}
          isLinking={isLinking}
          handleLinkToLead={handleLinkToLead}
          handleViewDetails={handleViewDetails}
          formatDate={(dateString) => new Date(dateString).toLocaleString()}
          formatUnixDate={(unixSecs) => new Date(unixSecs * 1000).toLocaleString()}
          truncateId={truncateId}
        />

        {showRawData && <ConversationRawData rawResponse={rawResponse} />}

        <ConversationDetailSidebar
          isOpen={sidebarOpen}
          onOpenChange={setSidebarOpen}
          selectedConversation={selectedConversation}
          conversationDetails={conversationDetails}
          isLoadingDetails={isLoadingDetails}
          audioData={audioData}
          isLoadingAudio={isLoadingAudio}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          handleFetchAudio={handleFetchAudio}
          truncateId={truncateId}
        />
      </CardContent>
    </Card>
  );
};

export default ConversationsList;
